// A club logo, scaled down in the browser before it is uploaded.
//
// ⚠️ A CONVENIENCE, NOT THE RULE. The server shrinks every club logo to 256 px
// whatever arrives (api/_logoImage.js), so nothing here is trusted. What this
// buys is the upload itself: a phone photo of a crest is 3–8 MB, over the
// endpoint's 2 MB limit, and used to be refused outright with "please use a
// smaller image" — which is exactly the job a computer should be doing.
//
// It stops at 512 px, twice the stored size, on purpose: a canvas downscale is a
// cruder filter than the server's, so the browser takes the big step and the
// server takes the last, careful one.
//
// Anything it cannot do well is passed through untouched for the server to
// judge: a GIF (a canvas would flatten an animation to one frame), an SVG (the
// server refuses it, and says so), a file the browser cannot decode, or a
// browser with no canvas. EXIF orientation is honoured by `createImageBitmap`
// with `imageOrientation: 'from-image'` and by modern `<img>` decoding alike.

export const CLIENT_LOGO_EDGE = 512;

const readDataUrl = (blob) => new Promise((res, rej) => {
  const fr = new FileReader();
  fr.onload = () => res(fr.result);
  fr.onerror = () => rej(new Error('Could not read that file.'));
  fr.readAsDataURL(blob);
});

async function decode(file) {
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch { /* fall through */ }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => res(img);
      img.onerror = () => rej(new Error('decode'));
      img.src = url;
    });
  } finally {
    // Safe once loaded: the decoded image no longer needs the object URL.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/**
 * Is this WebP animated? The VP8X header's animation flag (byte 20, bit 1).
 * Anything unreadable answers no, which only costs the shrink being tried.
 */
async function isAnimatedWebp(file) {
  try {
    const head = new Uint8Array(await file.slice(0, 21).arrayBuffer());
    const tag = String.fromCharCode(head[12], head[13], head[14], head[15]);
    return tag === 'VP8X' && (head[20] & 0x02) !== 0;
  } catch {
    return false;
  }
}

/**
 * Resolve a data URL for `file`, scaled so its longest edge is at most
 * `maxEdge`. Falls back to the file as it is whenever scaling is not possible.
 */
export async function shrinkLogoFile(file, maxEdge = CLIENT_LOGO_EDGE) {
  const type = String(file?.type || '');
  if (!/^image\/(png|jpeg|webp)$/.test(type)) return readDataUrl(file);
  // An ANIMATED WebP would come out of the canvas as one frame; send it as it
  // is and let the server shrink it, which keeps the frames (as it does GIFs).
  if (type === 'image/webp' && (await isAnimatedWebp(file))) return readDataUrl(file);
  let src;
  try { src = await decode(file); } catch { return readDataUrl(file); }
  const w = src.width || src.naturalWidth || 0;
  const h = src.height || src.naturalHeight || 0;
  if (!w || !h || Math.max(w, h) <= maxEdge) { src.close?.(); return readDataUrl(file); }

  const k = maxEdge / Math.max(w, h);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w * k));
  canvas.height = Math.max(1, Math.round(h * k));
  const ctx = canvas.getContext?.('2d');
  if (!ctx) { src.close?.(); return readDataUrl(file); }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, canvas.width, canvas.height);
  src.close?.();

  // A JPEG has no transparency to keep; everything else goes as PNG, which
  // every browser can encode and which keeps the alpha channel.
  const outType = type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
  const blob = await new Promise(res => canvas.toBlob(res, outType, 0.92));
  if (!blob || blob.size >= file.size) return readDataUrl(file);
  return readDataUrl(blob);
}
